// This is the entry point for the home component
import React, { FC, useCallback, useContext } from 'react';

// Redux
import { Dispatch } from 'redux';

// Styling
import injectSheet from 'react-jss';
import Dropdown from 'Components/Dropdown';
import { FilterSlider } from 'Components/FilterSlider';
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import withErrorBoundary from 'HOC/ErrorBoundary';
import styleSheet from './styles';

// Classes
import { Range } from '../../classes/filter.classes';

// Actions
import { setTestScoresValue } from '../actions/prospectProfile.actions';

// Components

interface Props {
  testScores: any;
  dispatch: Dispatch<any>;
  classes: any;
}

const options = [
  { label: 'Any', value: 'Any' },
  { label: 'SAT', value: 'SAT' },
  { label: 'ACT', value: 'ACT' },
  { label: 'SAT or ACT', value: 'SAT or ACT' },
  { label: 'SAT and ACT', value: 'SAT and ACT' },
];

// For Mixpane
const filterData = {
  filterCategory: 'Prospect Profile',
  filterName: '',
  filterValue: '',
  addToStore: true,
};

const TestScoresContainer: FC<Props> = (props: Props) => {
  const { testScores, dispatch, classes } = props;

  const {
    actions: { filterTrack },
  } = useContext(ProductAnalyticsContext);

  const onDropdownChange = (event: any) => {
    const { value } = event.target;

    const valueLowerCase = value.toLocaleLowerCase();
    // Combined Analytics Event
    switch (valueLowerCase) {
      case 'any':
        filterData.filterName = `TestScore - Any`;
        filterData.filterValue = value;
        break;
      case 'sat':
        filterData.filterName = `TestScore - SAT`;
        filterData.filterValue = '400 - 1600';
        break;
      case 'act':
        filterData.filterName = `TestScore - ACT`;
        filterData.filterValue = '1 - 36';
        break;
      case 'sat or act':
        filterData.filterName = `TestScore - SAT or ACT`;
        filterData.filterValue = 'Sat 400 - 1600 Act 1 - 36';
        break;
      case 'sat and act':
        filterData.filterName = `TestScore - SAT and ACT`;
        filterData.filterValue = 'Sat 400 - 1600 Act 1 - 36';
        break;

      default:
        break;
    }
    filterTrack(filterData);

    setTestScoresValue(
      dispatch,
      testScores,
      value,
      'selectionProspectProfileTestScores'
    );
  };

  const satRangeSliderChange = (values: number[]) => {
    const value = new Range(values[0], values[1]);
    // Combined Analytics Event
    filterTrack({
      filterCategory: 'Prospect Profile',
      filterName: `Test Scores - SAT`,
      filterValue: `${values[0]} - ${values[1]}`,
      addToStore: true,
    });
    setTestScoresValue(dispatch, testScores, { sat: value }, 'value');
  };

  const actRangeSliderChange = (values: number[]) => {
    const value = new Range(values[0], values[1]);
    // Combined Analytics Event
    filterTrack({
      filterCategory: 'Prospect Profile',
      filterName: `Test Scores -ACT`,
      filterValue: `${values[0]} - ${values[1]}`,
      addToStore: true,
    });
    setTestScoresValue(dispatch, testScores, { act: value }, 'value');
  };

  return (
    <div className={classes.container}>
      <Dropdown
        ariaLabel="Select SAT and/or ACT"
        items={options}
        width="109px"
        defaultValue={
          options[
            options.findIndex((option) => {
              return option.value === testScores.selection;
            })
          ]
        }
        onChange={useCallback(onDropdownChange, [testScores])}
      />
      <div
        className={
          (testScores.selection !== 'SAT' &&
            testScores.selection !== 'SAT or ACT' &&
            testScores.selection !== 'SAT and ACT' &&
            classes.hidden) ||
          classes.indented
        }
      >
        <div className={classes.testName}>SAT</div>
        <FilterSlider
          defaultValues={{
            handleOne: testScores.value.sat.lower,
            handleTwo: testScores.value.sat.upper,
          }}
          range={{ lowerBound: 400, upperBound: 1600 }}
          step={10}
          onSlideEnd={useCallback(satRangeSliderChange, [testScores])}
          ariaLabel="Select SAT Range"
        />
      </div>
      <div
        className={
          (testScores.selection !== 'ACT' &&
            testScores.selection !== 'SAT or ACT' &&
            testScores.selection !== 'SAT and ACT' &&
            classes.hidden) ||
          classes.indented
        }
      >
        <div className={classes.testName}>ACT</div>
        <FilterSlider
          defaultValues={{
            handleOne: testScores.value.act.lower,
            handleTwo: testScores.value.act.upper,
          }}
          range={{ lowerBound: 1, upperBound: 36 }}
          step={1}
          onSlideEnd={useCallback(actRangeSliderChange, [testScores])}
          ariaLabel="Select ACT Range"
        />
      </div>
    </div>
  );
};

export default withErrorBoundary(injectSheet(styleSheet)(TestScoresContainer));
